/* eslint-disable @typescript-eslint/no-explicit-any -- Supabase JSON/RPC + composer boundary; see mem://preferences/lint-debt.md */
import type {
  ContentPublishingConnector, ContentPublishingDispatch,
} from "../types";
import type { EmailConnectorConfig } from "../connectors";

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const isValidEmail = (value: string): boolean => EMAIL_RX.test(value.trim());

export const normalizeRecipientList = (input: string[] | null | undefined): string[] => {
  if (!Array.isArray(input)) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of input) {
    if (typeof raw !== "string") continue;
    const trimmed = raw.trim();
    if (!trimmed) continue;
    const lower = trimmed.toLowerCase();
    if (seen.has(lower)) continue;
    seen.add(lower);
    out.push(trimmed);
  }
  return out;
};

export const isEmailConnector = (connector: Pick<ContentPublishingConnector, "connector_type">) =>
  connector.connector_type === "email";

export const getEmailConnectorConfig = (
  connector: Pick<ContentPublishingConnector, "config">,
): EmailConnectorConfig => {
  const cfg = (connector.config ?? {}) as Partial<EmailConnectorConfig>;
  return {
    from_name: typeof cfg.from_name === "string" ? cfg.from_name : null,
    reply_to: typeof cfg.reply_to === "string" ? cfg.reply_to : null,
    recipients: normalizeRecipientList(Array.isArray(cfg.recipients) ? cfg.recipients : []),
    subject_template:
      typeof cfg.subject_template === "string" && cfg.subject_template.trim()
        ? cfg.subject_template
        : "{{title}}",
  };
};

export type EmailDispatchSnapshot = {
  recipients?: string[];
  subject?: string | null;
  recipient_count?: number;
  accepted_count?: number;
  rejected_count?: number;
  message_ids?: string[];
  rejections?: Array<{ email: string; reason: string }>;
};

export const isEmailDispatch = (
  dispatch: Pick<ContentPublishingDispatch, "request_snapshot">,
): boolean => {
  const req = (dispatch.request_snapshot ?? {}) as Record<string, unknown>;
  if (req.connector_type === "email") return true;
  if (req.kind === "email") return true;
  return Array.isArray(req.recipients);
};

export const summarizeEmailDispatch = (
  dispatch: Pick<ContentPublishingDispatch, "request_snapshot" | "response_snapshot">,
): {
  recipients: string[];
  subject: string | null;
  acceptedCount: number;
  rejectedCount: number;
  messageIds: string[];
  rejections: Array<{ email: string; reason: string }>;
  firstMessageId: string | null;
} => {
  const req = (dispatch.request_snapshot ?? {}) as EmailDispatchSnapshot;
  const res = (dispatch.response_snapshot ?? {}) as EmailDispatchSnapshot;
  const recipients = Array.isArray(req.recipients) ? req.recipients.filter((v): v is string => typeof v === "string") : [];
  const subject = typeof req.subject === "string" ? req.subject : null;
  const acceptedCount = typeof res.accepted_count === "number" ? res.accepted_count : 0;
  const rejectedCount = typeof res.rejected_count === "number" ? res.rejected_count : 0;
  const messageIds = Array.isArray(res.message_ids) ? res.message_ids.filter((v): v is string => typeof v === "string") : [];
  const rejections = Array.isArray(res.rejections)
    ? res.rejections.filter(
        (r): r is { email: string; reason: string } =>
          !!r && typeof r === "object" && typeof (r as any).email === "string" && typeof (r as any).reason === "string",
      )
    : [];
  return {
    recipients,
    subject,
    acceptedCount,
    rejectedCount,
    messageIds,
    rejections,
    firstMessageId: messageIds[0] ?? null,
  };
};

export const countEmailRecipientsThisMonth = (
  dispatches: ContentPublishingDispatch[],
  reference: Date = new Date(),
): number => {
  const start = new Date(reference.getFullYear(), reference.getMonth(), 1).getTime();
  let total = 0;
  for (const d of dispatches) {
    if (new Date(d.created_at).getTime() < start) continue;
    if (!isEmailDispatch(d)) continue;
    const req = (d.request_snapshot ?? {}) as EmailDispatchSnapshot;
    if (typeof req.recipient_count === "number") {
      total += req.recipient_count;
    } else if (Array.isArray(req.recipients)) {
      total += req.recipients.length;
    }
  }
  return total;
};
